import { InputAdornment } from "@mui/material";
import {
  Autocomplete,
  Button,
  Check,
  LoveHandles,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCreateTidLinkedAccount } from "@/api/graphql/billing/createTidLinkedAccount/hooks";
import { LinkableAccount } from "@/api/graphql/billing/getAccountsLinkableToTid/types";
import { useAddSnackbar } from "@/contexts/snackbar";
import { formatCustomerAccountNumber } from "@/helpers/account/formatCustomer/utils";
import { getHighlightedText } from "@/helpers/search/getHighlightedText";
import { useDebounce } from "@/hooks/useDebounce";

import { getAddTidLinkedAccountDrawerUi } from "./config";
import {
  MAX_VISIBLE_RESULTS,
  MIN_QUERY_LENGTH,
  RESULT_ITEM_HEIGHT_PX,
  SEARCH_DEBOUNCE_MS,
} from "./constants";
import { useAccountsLinkableToTid } from "./hooks";
import {
  StyledButtonsContainer,
  StyledDrawerContainer,
  StyledDrawerContent,
  StyledDrawerHeaderContainer,
  StyledOfficeIcon,
  StyledOptionContainer,
  StyledOptionLeftContainer,
  StyledOptionText,
  StyledSearchField,
  StyledSearchIcon,
} from "./styles";
import { AddTidLinkedAccountDrawerProps } from "./types";

export const AddTidLinkedAccountDrawer = ({
  accountId,
  context,
  typeId,
  createTidLinkedAccountFn,
  getAccountsLinkableToTidFn,
  onClose,
}: Readonly<AddTidLinkedAccountDrawerProps>) => {
  const { t: tCompanies } = useTranslation("companies");
  const addSnackbar = useAddSnackbar();

  const [inputValue, setInputValue] = useState("");
  const [selectedAccount, setSelectedAccount] =
    useState<LinkableAccount | null>(null);

  const debouncedQuery = useDebounce(inputValue, SEARCH_DEBOUNCE_MS);

  const { titleKey, dataTestIdPrefix, tidTypeCode } =
    getAddTidLinkedAccountDrawerUi(context);

  const { data, isFetching } = useAccountsLinkableToTid({
    getAccountsLinkableToTidFn,
    query: debouncedQuery,
    targetAccountId: accountId,
    tidTypeCode,
    minQueryLength: MIN_QUERY_LENGTH,
  });

  const { mutateAsync, isPending } = useCreateTidLinkedAccount({
    createTidLinkedAccountFn,
  });

  const options = data?.data ?? [];
  const hasMinLength = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const hasNoResults = hasMinLength && !isFetching && options.length === 0;

  const handleAdd = async () => {
    if (!selectedAccount || !typeId) {
      return;
    }

    try {
      await mutateAsync({
        accountId,
        linkedAccountId: selectedAccount.accountId,
        typeId,
      });
      addSnackbar({
        dataTestId: `accounts__account-details-${dataTestIdPrefix}_add-success-snackbar`,
        title: tCompanies("companies.tid.snackbar.add.success.title"),
        message: "",
        type: "success",
        isAutoClose: true,
      });
      onClose();
    } catch {
      addSnackbar({
        dataTestId: `accounts__account-details-${dataTestIdPrefix}_add-error-snackbar`,
        title: tCompanies("companies.tid.snackbar.add.error.title"),
        message: tCompanies("companies.tid.snackbar.add.error.message"),
        type: "error",
        isAutoClose: true,
      });
    }
  };

  return (
    <StyledDrawerContainer>
      <StyledDrawerHeaderContainer>
        <Text variant='title5'>{tCompanies(titleKey)}</Text>
      </StyledDrawerHeaderContainer>

      <LoveHandles>
        <StyledDrawerContent>
          <Autocomplete
            id={`${dataTestIdPrefix}_add-account-search`}
            dataTestId={`accounts__account-details-${dataTestIdPrefix}_add-drawer-search`}
            options={hasMinLength ? options : []}
            value={selectedAccount}
            onChange={(_event, newValue) =>
              setSelectedAccount(newValue as LinkableAccount | null)
            }
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) =>
              setInputValue(newInputValue)
            }
            loading={hasMinLength && isFetching}
            isOptionEqualToValue={(option, value) =>
              option.accountId === value.accountId
            }
            getOptionLabel={(option) => (option as LinkableAccount).accountName}
            filterOptions={(opts) => opts}
            renderOption={(props, option) => {
              const formattedNumber = formatCustomerAccountNumber(
                option.accountNumber,
              );

              return (
                <StyledOptionContainer
                  {...props}
                  key={option.accountId}
                  data-testid={`accounts__account-details-${dataTestIdPrefix}_add-drawer-option-${option.accountId}`}
                >
                  <StyledOptionLeftContainer>
                    <StyledOfficeIcon width={32} height={32} />
                    <StyledOptionText variant='text2'>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getHighlightedText(
                            option.accountName,
                            debouncedQuery,
                          ),
                        }}
                      />
                    </StyledOptionText>
                  </StyledOptionLeftContainer>
                  <StyledOptionText variant='text2'>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedText(
                          formattedNumber,
                          debouncedQuery,
                        ),
                      }}
                    />
                  </StyledOptionText>
                </StyledOptionContainer>
              );
            }}
            noOptionsText={
              hasNoResults
                ? tCompanies("companies.tid.drawer.add.search.noResults")
                : tCompanies("companies.tid.drawer.add.search.minChars")
            }
            slotProps={{
              listbox: {
                style: {
                  maxHeight: `${MAX_VISIBLE_RESULTS * RESULT_ITEM_HEIGHT_PX}px`,
                },
              },
            }}
            renderInput={(params) => (
              <StyledSearchField
                {...params}
                placeholder={tCompanies(
                  "companies.tid.drawer.add.search.placeholder",
                )}
                data-testid={`accounts__account-details-${dataTestIdPrefix}_add-drawer-search-input`}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <StyledSearchIcon width={18} height={18} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {selectedAccount && (
                        <InputAdornment position='end'>
                          <StyledOptionText variant='text2' $displayOnly>
                            {formatCustomerAccountNumber(
                              selectedAccount.accountNumber,
                            )}
                          </StyledOptionText>
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <StyledButtonsContainer>
            <Button
              variant='secondary'
              isLowercase
              buttonText={tCompanies("companies.tid.drawer.cancel")}
              dataTestId={`accounts__account-details-${dataTestIdPrefix}_add-drawer-cancel-button`}
              onClick={onClose}
            />
            <Button
              variant='primary'
              isLowercase
              isLoading={isPending}
              leftIcon={<Check width={24} height={24} color='#FFFFFF' />}
              buttonText={tCompanies("companies.tid.drawer.add.confirm")}
              dataTestId={`accounts__account-details-${dataTestIdPrefix}_add-drawer-confirm-button`}
              onClick={handleAdd}
            />
          </StyledButtonsContainer>
        </StyledDrawerContent>
      </LoveHandles>
    </StyledDrawerContainer>
  );
};
